import { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Lock, Loader2, AlertCircle, Package, Calendar, Mail, DollarSign } from "lucide-react";
import { Navigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface Order {
  id: string;
  created: number;
  amount: number;
  currency: string;
  status: string;
  customerEmail: string;
  description: string;
  metadata: Record<string, string>;
}

const OwnerDashboard = () => {
  // 🔒 SECURITY: Block access on published site - only allow in preview/development
  const isProduction = import.meta.env.PROD;
  const isPublishedSite = !window.location.hostname.includes('lovableproject.com');
  
  // If this is the published site, redirect to 404
  if (isProduction || isPublishedSite) {
    console.warn('🚫 Owner dashboard blocked on published site');
    return <Navigate to="/404" replace />;
  }

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoadingOrders, setIsLoadingOrders] = useState(false);
  const [ordersError, setOrdersError] = useState("");

  // Check localStorage on mount
  useEffect(() => {
    const authed = localStorage.getItem("ownerDashboardAuthed");
    if (authed === "true") {
      setIsAuthenticated(true);
    }
  }, []);

  // Fetch orders when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchOrders();
    }
  }, [isAuthenticated]);

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError("");
    setIsAuthenticating(true);

    try {
      const { data, error } = await supabase.functions.invoke('admin-orders', {
        body: { password },
      });

      if (error) throw error;

      if (data.error) {
        setAuthError(data.error);
        setIsAuthenticating(false);
        return;
      }

      // Success - store auth flag and set orders
      localStorage.setItem("ownerDashboardAuthed", "true");
      setIsAuthenticated(true);
      setOrders(data.orders || []);
    } catch (err) {
      console.error('Authentication error:', err);
      setAuthError("Failed to authenticate. Please try again.");
    } finally {
      setIsAuthenticating(false);
    }
  };

  const fetchOrders = async () => {
    setIsLoadingOrders(true);
    setOrdersError("");

    try {
      const storedPassword = localStorage.getItem("ownerDashboardPassword");
      
      const { data, error } = await supabase.functions.invoke('admin-orders', {
        body: { password: storedPassword || password },
      });

      if (error) throw error;

      if (data.error) {
        // Password invalid - clear auth
        localStorage.removeItem("ownerDashboardAuthed");
        localStorage.removeItem("ownerDashboardPassword");
        setIsAuthenticated(false);
        setAuthError("Session expired. Please log in again.");
        return;
      }

      setOrders(data.orders || []);
      // Store password for future refreshes
      if (password) {
        localStorage.setItem("ownerDashboardPassword", password);
      }
    } catch (err) {
      console.error('Error fetching orders:', err);
      setOrdersError("Failed to load orders. Please check configuration.");
    } finally {
      setIsLoadingOrders(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("ownerDashboardAuthed");
    localStorage.removeItem("ownerDashboardPassword");
    setIsAuthenticated(false);
    setPassword("");
    setOrders([]);
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  // Password form
  if (!isAuthenticated) {
    return (
      <>
        <Helmet>
          <title>Owner Dashboard - Samboards</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>

        <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
          <Card className="w-full max-w-md bg-slate-900/70 backdrop-blur-md border-slate-700/60 p-8">
            <div className="flex flex-col items-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-400 to-cyan-400 rounded-full flex items-center justify-center mb-4">
                <Lock className="w-8 h-8 text-slate-900" />
              </div>
              <h1 className="text-2xl font-bold text-slate-100">Owner Dashboard</h1>
              <p className="text-slate-400 text-sm mt-2">Enter password to access</p>
            </div>

            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div>
                <Input
                  type="password"
                  placeholder="Dashboard password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-slate-800/50 border-slate-700 text-slate-100 placeholder:text-slate-500"
                  disabled={isAuthenticating}
                  autoFocus
                />
              </div>

              {authError && (
                <Alert variant="destructive" className="bg-red-900/20 border-red-900/50">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{authError}</AlertDescription>
                </Alert>
              )}

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-emerald-400 to-cyan-400 text-slate-900 font-semibold hover:shadow-lg"
                disabled={isAuthenticating || !password}
              >
                {isAuthenticating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Authenticating...
                  </>
                ) : (
                  "Access Dashboard"
                )}
              </Button>
            </form>
          </Card>
        </div>
      </>
    );
  }

  // Orders dashboard
  return (
    <>
      <Helmet>
        <title>Owner Dashboard - Samboards</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 pt-24 pb-16 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-slate-100 mb-2">
                Recent Orders
              </h1>
              <p className="text-slate-400">
                {orders.length} successful orders from Stripe
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={fetchOrders}
                disabled={isLoadingOrders}
                variant="outline"
                className="border-slate-700 text-slate-300 hover:bg-slate-800"
              >
                {isLoadingOrders ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Refresh"
                )}
              </Button>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="border-red-900/50 text-red-400 hover:bg-red-900/20"
              >
                Logout
              </Button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <Card className="bg-slate-900/70 backdrop-blur-md border-slate-700/60 p-6">
              <div className="flex items-center gap-3">
                <Package className="w-8 h-8 text-emerald-400" />
                <div>
                  <p className="text-slate-400 text-sm">Total Orders</p>
                  <p className="text-2xl font-bold text-slate-100">{orders.length}</p>
                </div>
              </div>
            </Card>
            <Card className="bg-slate-900/70 backdrop-blur-md border-slate-700/60 p-6">
              <div className="flex items-center gap-3">
                <DollarSign className="w-8 h-8 text-emerald-400" />
                <div>
                  <p className="text-slate-400 text-sm">Total Revenue</p>
                  <p className="text-2xl font-bold text-slate-100">
                    {formatCurrency(
                      orders.reduce((sum, order) => sum + order.amount, 0),
                      'GBP'
                    )}
                  </p>
                </div>
              </div>
            </Card>
            <Card className="bg-slate-900/70 backdrop-blur-md border-slate-700/60 p-6">
              <div className="flex items-center gap-3">
                <Calendar className="w-8 h-8 text-emerald-400" />
                <div>
                  <p className="text-slate-400 text-sm">Latest Order</p>
                  <p className="text-lg font-bold text-slate-100">
                    {orders.length > 0 ? formatDate(orders[0].created).split(',')[0] : 'N/A'}
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* Error Alert */}
          {ordersError && (
            <Alert variant="destructive" className="mb-6 bg-red-900/20 border-red-900/50">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{ordersError}</AlertDescription>
            </Alert>
          )}

          {/* Orders Table */}
          {isLoadingOrders ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
              <span className="ml-3 text-slate-400">Loading orders...</span>
            </div>
          ) : orders.length === 0 ? (
            <Card className="bg-slate-900/70 backdrop-blur-md border-slate-700/60 p-12 text-center">
              <Package className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No orders found</p>
            </Card>
          ) : (
            <Card className="bg-slate-900/70 backdrop-blur-md border-slate-700/60 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700/60 hover:bg-slate-800/50">
                    <TableHead className="text-slate-300">Date</TableHead>
                    <TableHead className="text-slate-300">Customer Email</TableHead>
                    <TableHead className="text-slate-300">Amount</TableHead>
                    <TableHead className="text-slate-300">Status</TableHead>
                    <TableHead className="text-slate-300">Description</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.map((order) => (
                    <TableRow
                      key={order.id}
                      className="border-slate-700/60 hover:bg-slate-800/50"
                    >
                      <TableCell className="text-slate-200">
                        {formatDate(order.created)}
                      </TableCell>
                      <TableCell className="text-slate-200">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-slate-500" />
                          {order.customerEmail}
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-200 font-semibold">
                        {formatCurrency(order.amount, order.currency)}
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-900/30 text-emerald-400 border border-emerald-900/50">
                          {order.status}
                        </span>
                      </TableCell>
                      <TableCell className="text-slate-400 text-sm">
                        {order.description}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </div>
      </div>
    </>
  );
};

export default OwnerDashboard;
